import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Settings, Info, Apple, Link2, MapPin, BookOpen, Plus } from 'lucide-react';
import CalendarNamePicker from './CalendarNamePicker'; // (2 Nov 2026, decision 170)
import SettingsModal from './SettingsModal';
import UserGuideModal from './UserGuideModal';
import HostBoard from './HostBoard';
import { formatDateByPref, formatEventDateByPref, formatTime, formatShortDate, formatSignedInAs } from '../utils/formatting';
import StatusToast from './StatusToast';
import HistoryPanel from './HistoryPanel'; // (9 Oct 2026, 8.19/8.20, decision 58)
import EventAdminPollControls from './EventAdminPollControls'; // (26 Oct 2026, decision 145)
import EventAdminBell from './EventAdminBell'; // (26 Oct 2026, decision 145, part 3)
import useBalancedRows from '../utils/useBalancedRows'; // (30 Oct 2026, decision 160)

// Extracted from App.js (App.js modularization, Sep 2026) — the
// step === 'member-calendar' screen: a plain member's event list for one
// calendar, with poll-status badges and access to their own Settings.
//
// This is the screen that had the real permissions gap fixed on 4 Sep
// (plain members previously had no way to reach Settings at all) via the
// restrictToProfileTabs flag — kept unchanged here, just moved.
// (30 Sep 2026, H17) Apple devices open Apple Maps; everything else Google Maps.
const directionsUrl = ({ lat, lng }) => {
  const isApple = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent || '');
  return isApple
    ? `https://maps.apple.com/?daddr=${lat},${lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
};

// (21 Oct 2026, 8.42, decision 113) The day before a YYYY-MM-DD date, as YYYY-MM-DD.
const dayBefore = (isoDate) => {
  const iso = String(isoDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return isoDate;
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

// (23 Oct 2026, 8.43, decision 112) A YYYY-MM-DD date in full, e.g. "Sunday 18 October",
// read as it is so no time zone can shift the day. Same as PollPage.jsx's fullDayDate.
const fullDayDate = (isoDate) => {
  const iso = String(isoDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
};

const MemberCalendarStep = ({
  statusMessage,
  currentMemberCalendar,
  userEmail,
  preferredName,
  memberCalendars,
  onSwitchCalendar,
  isAuthenticated,
  onSignOut,
  showSettings,
  setShowSettings,
  settingsInitialTab,
  setSettingsInitialTab,
  isLoadingMemberEvents,
  memberEvents,
  showingPastEvents,
  onTogglePastEvents,
  isLoadingMemberPastEvents,
  memberPastEvents,
  pollHistoryCourses,
  userTier,
  showWeekday = true, // (24 Oct 2026, 8.40a, decision 100)
  onOpenPoll,
  apiBaseUrl,
  authToken,
  onStatusMessage,
  firstName,
  lastName,
  onSaveProfile,
  hostBoardRefreshToken, // (8 Oct 2026, 8.35) reload-on-return
  onAccessLost, // (8 Oct 2026, 8.35) a refusal from the hosting schedule
  // (26 Oct 2026, decision 145) For an Event Administrator's controls on their own event:
  // open the results page, and reload the events after a poll action.
  onViewResults,
  onEventsChanged,
  // (28 Oct 2026, decision 153) Someone with the right to create calendars who owns none:
  // "New Calendar" opens Create Calendar.
  canCreateCalendar = false,
  onNewCalendar,
  // (28 Oct 2026, decision 154) Calendars this person administers. When there are any,
  // the heading becomes the same drop-down as on the dashboard, so they can go back.
  adminCalendars = [],
  onChooseCalendar,
}) => {
  // (11 Sep 2026) Which list is actually on screen — past events reuse
  // every bit of rendering below (including the poll badge, which already
  // distinguishes open vs closed correctly), just from a different list.
  const activeEvents = showingPastEvents ? memberPastEvents : memberEvents;
  // (30 Oct 2026, decision 160) Phones: one row of top buttons when they fit, else the fewest even rows.
  const topButtonsRef = useRef(null);
  useBalancedRows(topButtonsRef);
  const activeIsLoading = showingPastEvents ? isLoadingMemberPastEvents : isLoadingMemberEvents;
  // (26 Sep 2026) H1: members had no Guide once signed in (the sign-in
  // panel's Guide can't know the calendar; the dashboard is GA-only).
  // Kept local: nothing else on this screen opens it.
  const [showGuide, setShowGuide] = useState(false);
  // (2 Oct 2026) A couple's secondary contact sees "Signed in as: Allan of
  // Turea & Allan". Asked per calendar (the same person can be a full member
  // of another one); cleared on every switch so the line never shows the
  // previous calendar's partner.
  const [secondaryOf, setSecondaryOf] = useState(null);
  const memberCalendarId = currentMemberCalendar?.id;
  useEffect(() => {
    setSecondaryOf(null);
    if (!memberCalendarId || !authToken || !apiBaseUrl) return undefined;
    let cancelled = false;
    fetch(`${apiBaseUrl}/api/calendars/${memberCalendarId}/me/identity`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.role === 'secondary') setSecondaryOf(d.primaryPreferredName || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [memberCalendarId, authToken, apiBaseUrl]);
  // (30 Sep 2026, H21) Members had no way to find the subscription links
  // (only the administrator's "Share with Your Group" panel showed them).
  // Built exactly as App.js builds them for the dashboard.
  // (3 Oct 2026, F1) Each person now has their OWN link (one per calendar),
  // asked for when the calendar opens; the calendar's shared link is retired.
  const [feedLink, setFeedLink] = useState(null); // { feedUrl, webcalUrl }
  useEffect(() => {
    setFeedLink(null);
    if (!memberCalendarId || !authToken || !apiBaseUrl) return undefined;
    let cancelled = false;
    fetch(`${apiBaseUrl}/api/calendars/${memberCalendarId}/me/feed-link`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && d.feedUrl) setFeedLink(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [memberCalendarId, authToken, apiBaseUrl]);
  // (9 Oct 2026, 8.19/8.20, decision 53) A "History" button on calendars that keep a
  // calendar history; asked per calendar, hidden until the answer is yes.
  const [historyEnabled, setHistoryEnabled] = useState(false);
  // (10 Oct 2026, decision 79; amends 53) On a calendar whose history shows the menu
  // (Hot Club), "View past events" is hidden: the History page shows every past
  // meeting's dishes, with who brought them where known, and View past events could
  // contradict it (meetings from the club's log have no poll, so it showed "—").
  // Calendars without a history, or with the menu off, keep View past events.
  const [historyShowsMenu, setHistoryShowsMenu] = useState(false);
  const [showHistory, setShowHistory] = useState(false); // (decision 58) in place of the events, like View past events
  useEffect(() => {
    setHistoryEnabled(false);
    setHistoryShowsMenu(false);
    setShowHistory(false);
    if (!memberCalendarId || !authToken || !apiBaseUrl) return undefined;
    let cancelled = false;
    const getJson = (url) => fetch(url, { headers: { 'Authorization': `Bearer ${authToken}` } })
      .then((r) => (r.ok ? r.json() : null));
    const base = `${apiBaseUrl}/api/calendars/${memberCalendarId}/calendar-history`;
    getJson(`${base}?check=1`)
      .then((d) => {
        if (cancelled || !d || d.enabled !== true) return null;
        setHistoryEnabled(true);
        // The quick check may not say whether the menu is shown; if not, the full
        // History route does (asked only on calendars that keep a history).
        if (typeof d.showMenu === 'boolean') return d;
        return getJson(base);
      })
      .then((d) => { if (!cancelled && d && d.showMenu === true) setHistoryShowsMenu(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [memberCalendarId, authToken, apiBaseUrl]);
  const hidePastEvents = historyEnabled && historyShowsMenu;
  // A member who was looking at past events when this calendar turned out to hide them
  // (e.g. after switching calendar) goes back to the upcoming events.
  useEffect(() => {
    if (hidePastEvents && showingPastEvents) onTogglePastEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidePastEvents]);
  // (23 Oct 2026, 8.43, decision 109) One upcoming event's card; isNext highlights it.
  const renderEventCard = (evt, isNext) => (
      // (24 Sep 2026) data-event-uid: lets App.js bring this card back into
      // view on return from the poll or results page (see rememberScroll there).
      // (23 Oct 2026, 8.43, decision 109) The next event is highlighted.
      <div
        key={evt.eventUid}
        data-event-uid={evt.eventUid}
        className={isNext
          ? 'bg-white border-2 border-blue-400 rounded-xl p-4 shadow-md'
          : 'bg-white border border-gray-200 rounded-xl p-4'}
      >
        {isNext && (
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1">Next event</p>
        )}
                {/* (23 Sep 2026) flex-wrap: on a narrow phone the poll pill drops
                    below the title/date instead of squeezing them. */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800">{evt.title}</p>
                    <p className="text-xs text-gray-500">
                      {/* (24 Oct 2026, 8.40a, decision 100) With the day of the week unless unticked. */}
                      {formatEventDateByPref(evt.eventDate, userTier?.preferred_date_format, showWeekday)}{evt.startTime ? ` · ${formatTime(evt.startTime)}` : ''}
                    </p>
                    {/* (1 Oct 2026, L1) One Location line, the same as on the
                        administrator's cards: pin, the event's Location text,
                        then "Directions" when the host has a saved location
                        (H17). Location moved here from the date line above. */}
                    {(evt.location || evt.hostLocation) && (
                      <p className="flex items-center gap-1.5 flex-wrap mt-0.5 text-xs text-gray-500">
                        <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                        {evt.location && <span>{evt.location}</span>}
                        {evt.location && evt.hostLocation && <span className="text-gray-400">·</span>}
                        {evt.hostLocation && (
                          <a
                            href={directionsUrl(evt.hostLocation)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-700 hover:underline"
                          >
                            Directions
                          </a>
                        )}
                      </p>
                    )}
                    {/* (26 Oct 2026, decision 145) The event's Event Administrator, so
                        members know whom to ask. */}
                    {evt.eventAdmin && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Host: {evt.eventAdmin.name || 'the host'} (event administrator)
                      </p>
                    )}
                  </div>
                  {evt.poll ? (
                    <button
                      type="button"
                      onClick={() => onOpenPoll(evt.eventUid, evt.poll.status)}
                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                        evt.poll.status === 'open' ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {/* (23 Sep 2026) Phase-terminology standardisation (rows 3/3b):
                          "In Phase 1 (offers)" / "In Phase 2 (confirm)", plus the
                          same amber dot as PollPage's filled course tab once this
                          member has replied. The reply timestamp moved off this
                          pill onto the poll page itself ("You last responded on
                          ..."); "please respond by" is shown only until a reply
                          exists. */}
                      {evt.poll.status === 'open' ? (
                        <>
                          {evt.poll.phase === 'offer' ? 'In Phase 1 (offers)' : 'In Phase 2 (confirm)'}
                          {evt.poll.hasResponded ? (
                            <span
                              className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 ml-1.5 align-middle"
                              aria-label="You have replied"
                              title="You have replied"
                            />
                          ) : (
                            // (21 Oct 2026, 8.42, decision 113) The day before the poll closes:
                            // it closes at 7 a.m., so answers belong to the day before.
                            evt.poll.closesOn ? ` · please respond by ${formatShortDate(dayBefore(evt.poll.closesOn))}` : ''
                          )}
                        </>
                      ) : 'Poll closed · View results'}
                    </button>
                  ) : evt.pollOpensOn ? (
                    // (23 Oct 2026, 8.43, decision 112) When Phase 1 will open by itself.
                    <span className="text-xs text-gray-500">Poll phase 1 (offers) opens {fullDayDate(evt.pollOpensOn)}</span>
                  ) : (
                    <span className="text-xs text-gray-400">No poll yet</span>
                  )}
                </div>
                {/* (23 Oct 2026, 8.43, decision 109) The next event's poll, one tap away. */}
                {isNext && evt.poll && evt.poll.status === 'open' && (
                  <button
                    type="button"
                    onClick={() => onOpenPoll(evt.eventUid, evt.poll.status)}
                    className="w-full mt-3 rounded-lg py-2.5 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    Answer the poll
                  </button>
                )}
                {/* (26 Oct 2026, decision 145) The signed-in person runs this event's poll. */}
                {evt.isEventAdmin && currentMemberCalendar && (
                  <EventAdminPollControls
                    evt={evt}
                    calendarId={currentMemberCalendar.id}
                    apiBaseUrl={apiBaseUrl}
                    authToken={authToken}
                    onStatusMessage={onStatusMessage}
                    onChanged={onEventsChanged}
                    onViewResults={onViewResults}
                  />
                )}
              </div>
  );
  const feedUrl = feedLink ? feedLink.feedUrl : null;
  const webcalUrl = feedLink ? feedLink.webcalUrl : null;
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50" style={{ minHeight: '100dvh' }}>
      <StatusToast statusMessage={statusMessage} />
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-2 sm:py-4 flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-blue-100 p-2 rounded-lg shrink-0">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              {adminCalendars.length > 0 && onChooseCalendar ? (
                // (28 Oct 2026, decision 154) The dashboard's drop-down: choosing a calendar
                // you administer shows its dashboard.
                // (2 Nov 2026, decision 170) The name wraps rather than being cut short.
                <CalendarNamePicker
                  name={currentMemberCalendar?.name}
                  value={currentMemberCalendar ? `member:${currentMemberCalendar.id}` : ''}
                  onChange={(e) => onChooseCalendar(e.target.value)}
                  className="sm:max-w-xs"
                >
                  <optgroup label="Calendars you administer">
                    {adminCalendars.map((cal) => (
                      <option key={cal.id} value={cal.id}>{cal.is_deputy ? `⚑ ${cal.name}` : cal.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Calendars you're a member of">
                    {memberCalendars.map((cal) => (
                      <option key={`member:${cal.id}`} value={`member:${cal.id}`}>{cal.name}</option>
                    ))}
                  </optgroup>
                </CalendarNamePicker>
              ) : (
                <h1 className="text-xl font-bold text-gray-800 leading-tight break-words">{currentMemberCalendar?.name}</h1>
              )}
              <p className="text-xs text-gray-500">{showHistory ? 'History' : showingPastEvents ? 'Past events' : 'Upcoming events'}</p>
              {userEmail && (
                <p className="text-xs text-gray-400 truncate">Signed in as: {formatSignedInAs(preferredName, firstName, lastName, userEmail, secondaryOf)}</p>
              )}
            </div>
          </div>
          {/* (24 Oct 2026, decision 135) On phones the buttons sit on two rows, as on the
              administrator's dashboard (decision 133, amended): View past events, History,
              Settings | Switch calendar, Sign Out, Guide. The order-N classes apply below
              sm only; wider screens keep the order below. Row gaps come from each
              button's bottom margin, so the line break adds no space of its own.
              Settings and Guide now have a border like the other buttons. */}
          <div ref={topButtonsRef} className="flex flex-wrap items-center gap-x-1.5 gap-y-0 sm:gap-2 -mb-1.5 sm:mb-0">
            {/* (decision 79) Not on calendars whose history shows the menu. */}
            {!hidePastEvents && (
              <button
                onClick={() => {
                  // From History, this button shows the past events.
                  if (showHistory) { setShowHistory(false); if (!showingPastEvents) onTogglePastEvents(); }
                  else onTogglePastEvents();
                }}
                className="order-1 sm:order-none mb-1.5 sm:mb-0 px-1.5 sm:px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition whitespace-nowrap"
              >
                {showingPastEvents && !showHistory ? 'Back to upcoming' : 'View past events'}
              </button>
            )}
            {historyEnabled && (
              <button
                // (decision 58) While History is showing, this shows as selected and does
                // nothing; Back on the History page returns to the events.
                onClick={() => setShowHistory(true)}
                aria-current={showHistory ? 'page' : undefined}
                className={`order-2 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1.5 text-xs font-medium border rounded-lg transition whitespace-nowrap ${showHistory ? 'bg-blue-50 border-blue-300 text-blue-700 cursor-default' : 'text-gray-600 hover:text-gray-900 border-gray-300 hover:bg-gray-50'}`}
              >
                <BookOpen className="w-3.5 h-3.5" />
                History
              </button>
            )}
            {/* (28 Oct 2026, decision 153) Only for someone who may create calendars;
                on phones, first on the second row. */}
            {canCreateCalendar && onNewCalendar && (
              <button
                onClick={onNewCalendar}
                className="order-5 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 bg-blue-600 text-white px-1.5 sm:px-2 py-1.5 rounded-lg hover:bg-blue-700 transition text-xs font-medium whitespace-nowrap"
              >
                <Plus className="w-3.5 h-3.5" />
                New Calendar
              </button>
            )}
            {/* (28 Oct 2026, decision 154) Not needed where the heading is the drop-down. */}
            {memberCalendars.length > 1 && adminCalendars.length === 0 && (
              <button
                onClick={onSwitchCalendar}
                className="order-5 sm:order-none mb-1.5 sm:mb-0 px-1.5 sm:px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition whitespace-nowrap"
              >
                Switch calendar
              </button>
            )}
            {isAuthenticated && (
              <button
                onClick={onSignOut}
                className="order-6 sm:order-none mb-1.5 sm:mb-0 px-1.5 sm:px-2 py-1.5 text-xs text-red-600 hover:text-red-700 font-medium border border-red-300 rounded-lg hover:bg-red-50 transition whitespace-nowrap"
              >
                Sign Out
              </button>
            )}
            <button
              onClick={() => setShowGuide(true)}
              className="order-7 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition whitespace-nowrap"
            >
              <Info className="w-3.5 h-3.5" />
              Guide
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="order-3 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition whitespace-nowrap"
            >
              <Settings className="w-3.5 h-3.5" />
              Settings
            </button>
            {/* (30 Oct 2026, decision 160, replacing 135's fixed break after Settings) Phones only:
                useBalancedRows shows this break where it splits the buttons into two rows. */}
            <div data-row-break aria-hidden="true" className="hidden h-0" />
            {/* (26 Oct 2026, decision 145, part 3) An Event Administrator's notices, with the
                WhatsApp messages ready to post; shown only while there is one. */}
            {(memberEvents || []).some((e) => e.isEventAdmin) && currentMemberCalendar && (
              <div className="order-8 sm:order-none mb-1.5 sm:mb-0">
                <EventAdminBell
                  calendarId={currentMemberCalendar.id}
                  apiBaseUrl={apiBaseUrl}
                  authToken={authToken}
                  formatDate={(d) => formatDateByPref(d, userTier?.preferred_date_format)}
                  onStatusMessage={onStatusMessage}
                  refreshKey={memberEvents}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* (9 Oct 2026, 8.19/8.20, decision 58) History, in place of the events */}
      {showHistory && historyEnabled && currentMemberCalendar && (
        <HistoryPanel
          calendar={{ id: currentMemberCalendar.id, name: currentMemberCalendar.name }}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          formatEventDate={(d) => formatDateByPref(d, userTier?.preferred_date_format)}
          onStatusMessage={onStatusMessage}
          onBack={() => setShowHistory(false)}
        />
      )}
      <div className={`mx-auto px-4 py-8 ${showingPastEvents ? 'max-w-5xl' : 'max-w-2xl'} ${showHistory && historyEnabled ? 'hidden' : ''}`}>
        {activeIsLoading ? (
          <p className="text-sm text-gray-500">{showingPastEvents ? 'Loading past events…' : 'Loading events…'}</p>
        ) : activeEvents.length === 0 ? (
          <p className="text-sm text-gray-500">
            {showingPastEvents ? 'No past events on this calendar yet.' : 'No upcoming events on this calendar yet.'}
          </p>
        ) : showingPastEvents ? (
          // (11 Sep 2026) Aggregated dish-history. A wide table reads well
          // on larger screens, but forces horizontal scrolling on a phone
          // — below md, each event becomes a stacked card instead, one
          // course per line, mirroring PollResultsPage's own "What
          // everyone brought" layout for a single event. Same data drives
          // both; this is a pure CSS breakpoint swap, not a second fetch.
          <>
            <div className="md:hidden space-y-3">
              {activeEvents.map((evt) => (
                <div key={evt.eventUid} data-event-uid={evt.eventUid} className="bg-white border border-gray-200 rounded-xl p-4">
                  {evt.pollStatus ? (
                    <button
                      type="button"
                      onClick={() => onOpenPoll(evt.eventUid, evt.pollStatus)}
                      className="text-blue-600 hover:underline font-semibold text-left block"
                    >
                      {evt.title}
                    </button>
                  ) : (
                    <p className="font-semibold text-gray-800">{evt.title}</p>
                  )}
                  <p className="text-xs text-gray-500 mb-3">
                    {formatDateByPref(evt.eventDate, userTier?.preferred_date_format)}
                    {evt.notes ? ` (${evt.notes})` : ''}
                  </p>
                  <div className="space-y-2">
                    {pollHistoryCourses.map((course) => {
                      const dishes = evt.dishesByCourse[course] || [];
                      return (
                        <div key={course}>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{course}</p>
                          {dishes.length === 0 ? (
                            <p className="text-sm text-gray-400 italic">Nothing offered for this course.</p>
                          ) : (
                            <ul className="space-y-0.5">
                              {dishes.map((d, i) => (
                                <li key={i} className="text-sm text-gray-700">{d.description} <span className="text-gray-400">by {d.name}</span></li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto bg-white border border-gray-200 rounded-xl">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Event</th>
                  {pollHistoryCourses.map((course) => (
                    <th key={course} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">{course}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeEvents.map((evt) => (
                  <tr key={evt.eventUid} data-event-uid={evt.eventUid} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2 text-gray-600">
                      {formatDateByPref(evt.eventDate, userTier?.preferred_date_format)}
                      {evt.notes ? ` (${evt.notes})` : ''}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {evt.pollStatus ? (
                        <button
                          type="button"
                          onClick={() => onOpenPoll(evt.eventUid, evt.pollStatus)}
                          className="text-blue-600 hover:underline font-medium text-left"
                        >
                          {evt.title}
                        </button>
                      ) : (
                        <span className="text-gray-800 font-medium">{evt.title}</span>
                      )}
                    </td>
                    {pollHistoryCourses.map((course) => {
                      const dishes = evt.dishesByCourse[course] || [];
                      return (
                        <td key={course} className="px-3 py-2 text-gray-700">
                          {dishes.length === 0 ? (
                            <span className="text-gray-400 italic">—</span>
                          ) : (
                            <ul className="space-y-1">
                              {dishes.map((d, i) => (
                                <li key={i}>{d.description} <span className="text-gray-400">by {d.name}</span></li>
                              ))}
                            </ul>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        ) : (
          // (23 Oct 2026, 8.43, decision 109) The next event first, highlighted, then the
          // later events (on a calendar with a hosting schedule, only those whose host is
          // confirmed: the rest are on the schedule below, with "Event, host wanted" and
          // Offer to host; members' subscribed calendars still hold every event).
          <div className="space-y-3">
            {activeEvents.map((evt, i) => {
              if (i > 0 && evt.hostWanted) return null;
              return renderEventCard(evt, i === 0);
            })}
          </div>
        )}
        {/* (20 Sep 2026) Host schedule board — members see every month in every
            state and can offer to host. Renders nothing when this calendar has
            no board (decided by the server).
            (23 Oct 2026, 8.43, decision 109) Now after the events, not before them. */}
        {!showingPastEvents && currentMemberCalendar && (
          <div className={activeEvents.length > 0 && !activeIsLoading ? 'mt-6' : ''}>
            <HostBoard
              calendar={currentMemberCalendar}
              apiBaseUrl={apiBaseUrl}
              showStatus={onStatusMessage}
              refreshToken={hostBoardRefreshToken}
              onAccessLost={onAccessLost}
            />
          </div>
        )}
      </div>

      {/* (30 Sep 2026, H21) Subscription links for members, collapsed by
          default (most members subscribe once and never need it again). */}
      {!showingPastEvents && !showHistory && feedUrl && (
        <div className="max-w-2xl mx-auto px-4 pb-8 -mt-4">
          <details className="bg-white border border-gray-200 rounded-xl p-4">
            <summary className="cursor-pointer text-base font-semibold text-blue-800">
              Subscribe to add this calendar to your phone, tablet and/or computer
            </summary>
            <p className="text-xs text-gray-600 mt-3">
              You only need to subscribe once. New and changed events will then appear in your device's calendar automatically, and without any intervention by yourself.
            </p>
            {/* (2 Oct 2026, S1) The feed needs no sign-in, so the link itself
                gives access (Neil's wording). */}
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
              {/* (3 Oct 2026, F1) The link is now this person's own. */}
              This link is personal to you. Sharing it with non-calendar members could expose personal data such as calendar appointments and meeting locations - please do not distribute.
            </p>
            <div className="mt-3 space-y-3">
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">iPhone or Mac</p>
                <a
                  href={webcalUrl}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Apple className="w-4 h-4" />
                  Subscribe (iPhone/Mac)
                </a>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">Google Calendar</p>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(feedUrl)
                      .then(() => onStatusMessage('Address copied', 'success'))
                      .catch(() => onStatusMessage('Could not copy the address', 'error'));
                  }}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition w-full justify-center text-sm font-medium"
                >
                  <Link2 className="w-4 h-4" />
                  Copy the calendar address
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  See the User Guide appendix for detailed instructions.
                </p>
              </div>
            </div>
          </details>
        </div>
      )}

      {showSettings && (
        <SettingsModal
          isOpen={showSettings}
          onClose={() => { setShowSettings(false); setSettingsInitialTab('profile'); }}
          userEmail={userEmail}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onStatusMessage={onStatusMessage}
          userTier={userTier}
          initialTab={settingsInitialTab}
          currentCalendar={currentMemberCalendar}
          firstName={firstName}
          lastName={lastName}
          onSaveProfile={onSaveProfile}
          restrictToProfileTabs={true}
        />
      )}

      {/* (26 Sep 2026) H1: calendar guide first (when set), then the general
          how-to-subscribe guide. Pricing hidden: a plain member isn't choosing
          a plan, and its "open the Guide from your dashboard" nudge would be
          wrong here. */}
      <UserGuideModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        showPricing={false}
        calendarGuide={currentMemberCalendar?.guide_url ? { name: currentMemberCalendar.name, url: currentMemberCalendar.guide_url } : null}
      />
    </div>
  );
};

export default MemberCalendarStep;
